import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getVideoById, getRelatedVideos, categories } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const video = getVideoById(id || '');

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h2>
          <p className="text-foreground-500 mb-6">抱歉，您要查看的内容不存在或已下架</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = getRelatedVideos(video);
  const categoryInfo = categories.find((c) => c.slug === video.categorySlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: video.category, href: `/category/${video.categorySlug}` },
            { label: video.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full lg:w-72">
              <div className="relative rounded-lg overflow-hidden bg-background-200" style={{ aspectRatio: '2/3' }}>
                <img
                  src={video.image}
                  alt={video.title}
                  title={`${video.title} - 西瓜视频免费在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {video.isHot && (
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-primary-500 text-background-50">
                    热播中
                  </span>
                )}
                {video.isNew && (
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-accent-500 text-background-50">
                    最新
                  </span>
                )}
                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600">
                  {video.category}
                </span>
                <span className="text-sm text-foreground-500">{video.year}</span>
              </div>

              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500 text-lg"></i>
                  <span className="text-lg font-bold text-foreground-900">{video.rating}</span>
                  <span className="text-sm text-foreground-500">/ 10</span>
                </div>
                <span className="text-sm text-foreground-500">
                  {video.views > 10000
                    ? `${(video.views / 10000).toFixed(0)}万次播放`
                    : `${video.views}次播放`}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 flex-shrink-0">导演：</span>
                  <span className="text-sm text-foreground-800">{video.director}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 flex-shrink-0">主演：</span>
                  <span className="text-sm text-foreground-800">{video.actors.join(' / ')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 flex-shrink-0">地区：</span>
                  <span className="text-sm text-foreground-800">{video.region}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 flex-shrink-0">时长：</span>
                  <span className="text-sm text-foreground-800">{video.duration}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 flex-shrink-0">状态：</span>
                  <span className="text-sm text-foreground-800">{video.updateStatus}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <Link
                to={`/article/${video.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
            </div>
          </div>

          <div className="mt-8 md:mt-10">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">剧情简介</h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl whitespace-pre-line">
              {video.longDescription}
            </p>
          </div>

          {relatedVideos.length > 0 && (
            <section className="mt-10 md:mt-12">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} size="sm" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}